import { getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { getFirebaseMessaging } from './firebaseConfig';
import { apiPost } from './api';

const VAPID_KEY = "BDRuxFr93-KV0z_lbS8MOFELR3FGU4ER8YGr5NvvZ8qka131NiBNkBjvSMwaeQY9wS5HIX3IG_xkUzog-KzL8Ao";
const FCM_TOKEN_KEY = 'fcm_token';

type MessageListener = (payload: MessagePayload) => void;

class FCMService {
  private messageListeners: MessageListener[] = [];
  private isInitialized = false;

  /**
   * Initialize FCM and request notification permission
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      const messaging = getFirebaseMessaging();
      if (!messaging) {
        console.warn('Firebase Messaging not available');
        return;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission not granted');
        return;
      }

      // Get FCM token
      const token = await this.getFCMToken();
      if (token) {
        // Send token to backend
        await this.sendTokenToBackend(token);
        
        // Store token locally
        this.storeToken(token);
      }

      // Listen for foreground messages
      onMessage(messaging, (payload) => {
        this.notifyListeners(payload);
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize FCM:', error);
    }
  }

  /**
   * Get FCM token
   */
  private async getFCMToken(): Promise<string | null> {
    try {
      const messaging = getFirebaseMessaging();
      if (!messaging) {
        return null;
      }

      const token = await getToken(messaging, { vapidKey: VAPID_KEY });
      return token;
    } catch (error) {
      console.error('Failed to get FCM token:', error);
      return null;
    }
  }

  /**
   * Send FCM token to backend
   */
  private async sendTokenToBackend(token: string): Promise<void> {
    try {
      await apiPost('/api/auth/fcm-token', { fcm_token: token });
      console.log('FCM token sent to backend successfully');
    } catch (error) {
      console.error('Failed to send FCM token to backend:', error);
    }
  }

  /**
   * Store FCM token in localStorage
   */
  private storeToken(token: string): void {
    localStorage.setItem(FCM_TOKEN_KEY, token);
  }

  /**
   * Get stored FCM token
   */
  getStoredToken(): string | null {
    return localStorage.getItem(FCM_TOKEN_KEY);
  }

  /**
   * Clear stored FCM token
   */
  clearToken(): void {
    localStorage.removeItem(FCM_TOKEN_KEY);
  }

  /**
   * Subscribe to foreground messages
   */
  onMessage(listener: MessageListener): () => void {
    this.messageListeners.push(listener);

    // Return unsubscribe function
    return () => {
      this.messageListeners = this.messageListeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of a new message
   */
  private notifyListeners(payload: MessagePayload): void {
    this.messageListeners.forEach(listener => listener(payload));
  }

  /**
   * Refresh FCM token (useful after login)
   */
  async refreshToken(): Promise<void> {
    try {
      const token = await this.getFCMToken();
      if (token) {
        await this.sendTokenToBackend(token);
        this.storeToken(token);
      }
    } catch (error) {
      console.error('Failed to refresh FCM token:', error);
    }
  }
}

export const fcmService = new FCMService();

